import { useQuery } from '@tanstack/react-query';
import { listActions } from '@/api/real-browsers';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';

const KIND_TONE: Record<string, string> = {
  ok:     'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30',
  failed: 'bg-orange-500/10  text-orange-300  ring-orange-500/30',
};

/**
 * Append-only operator audit. Every dashboard action (and every VNC
 * connect/disconnect, and every heartbeat-detected death) lands here
 * with 30-day TTL.
 */
export default function Audit() {
  const { data: actions = [], isLoading } = useQuery({
    queryKey: ['actions'],
    queryFn: () => listActions({ limit: 200 }),
    refetchInterval: 5_000,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Audit</h1>
        <p className="text-sm text-slate-500">
          Every dashboard / agent action against the fleet. 30-day TTL.
        </p>
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-500">loading…</div>
      ) : actions.length === 0 ? (
        <div className="text-sm text-slate-500">No actions yet.</div>
      ) : (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl divide-y divide-slate-800">
          {actions.map(a => (
            <div key={a.id} className="p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
              <span className={cn(
                'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 sm:col-span-1 inline-block w-fit',
                KIND_TONE[a.outcome] ?? KIND_TONE.failed,
              )}>
                {a.outcome}
              </span>
              <span className="font-mono text-sm sm:col-span-2">{a.kind}</span>
              <span className="font-mono text-xs text-slate-400 sm:col-span-4 truncate" title={a.instance_id}>
                {a.instance_id}
              </span>
              <span className="text-xs text-slate-500 sm:col-span-2">{a.actor}</span>
              <span className="text-xs text-slate-500 sm:col-span-3 sm:text-right">
                {relativeTime(a.ts)}
              </span>
              {a.error && (
                <div className="sm:col-span-12 text-xs text-red-300">
                  {a.error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
